/**
 * @-mentions in the comment composer, as pure functions.
 *
 * A mention is two things that must agree: the words `@Name` in the comment's
 * body, which is what a person and an agent READ, and an entry `{kind, id}` in
 * the comment's `mentions`, which is what the server acts on (the `toMe` that
 * wakes an agent, the email that reaches a person). The composer keeps the
 * entries the person PICKED from the list; before posting, `liveMentions` drops
 * any whose `@Name` they have since deleted from the text, so a comment never
 * notifies someone it no longer names. The server re-checks every id.
 *
 * Rendering goes the other way: `segmentBody` finds each mention's `@Name` in
 * a posted body and hands the sidebar text and chip pieces.
 */

import type { MentionCandidate, MentionRef } from './types'

/** The `@query` being typed at the caret, if there is one. */
export interface MentionQuery {
  /** Index of the `@`. */
  start: number
  /** What follows it, up to the caret. */
  query: string
}

/** Longest `@query` still treated as a search, rather than prose with an @. */
const MAX_QUERY = 40

/**
 * Is the person typing a mention at `caret`?
 *
 * An `@` counts when it starts the text or follows whitespace or an opening
 * bracket or quote -- so `pat@example.com` never opens the list -- and the
 * query after it has no line break. It may contain spaces: people's names do,
 * and "@Euan J" narrowing to Euan Jardine is the point.
 */
export function mentionQuery(value: string, caret: number): MentionQuery | null {
  const before = value.slice(0, caret)
  const at = before.lastIndexOf('@')
  if (at === -1) return null
  if (at > 0 && !/[\s([{"'“‘]/.test(before[at - 1])) return null
  const query = before.slice(at + 1)
  if (query.length > MAX_QUERY || /[\n\r@]/.test(query)) return null
  // Two spaces in a row, or a leading space, is prose, not a name.
  if (/^\s|\s\s/.test(query)) return null
  return { start: at, query }
}

/**
 * The candidates matching a query: names starting with it first, then names
 * with a WORD starting with it, then anywhere. Case- and accent-insensitive.
 */
export function filterCandidates(candidates: MentionCandidate[], query: string, limit = 8): MentionCandidate[] {
  const fold = (s: string) =>
    s
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
  const q = fold(query.trim())
  if (!q) return candidates.slice(0, limit)
  const scored: { c: MentionCandidate; rank: number; i: number }[] = []
  candidates.forEach((c, i) => {
    // An agent is found by its person as well as its name: "@euan" offers
    // Euan and the Claude acting for Euan.
    const hay = [c.name, c.forName ?? '', c.detail ?? ''].map(fold)
    let rank = -1
    for (const h of hay) {
      if (h.startsWith(q)) rank = Math.max(rank, 3)
      else if (h.split(/[\s,()]+/).some((w) => w.startsWith(q))) rank = Math.max(rank, 2)
      else if (h.includes(q)) rank = Math.max(rank, 1)
    }
    if (rank > 0) scored.push({ c, rank, i })
  })
  scored.sort((a, b) => b.rank - a.rank || a.i - b.i)
  return scored.slice(0, limit).map((s) => s.c)
}

/**
 * Replace the `@query` at `[start, caret)` with `@Name ` and put the caret
 * after the space.
 */
export function insertMention(
  value: string,
  start: number,
  caret: number,
  name: string,
): { value: string; caret: number } {
  const before = value.slice(0, start)
  const after = value.slice(caret)
  const inserted = `@${name}` + (after.startsWith(' ') ? '' : ' ')
  return { value: before + inserted + after, caret: before.length + inserted.length }
}

/** The picked mentions whose `@Name` is still in the text, in order, once each. */
export function liveMentions(body: string, picked: MentionRef[]): MentionRef[] {
  const seen = new Set<string>()
  const out: MentionRef[] = []
  for (const m of picked) {
    const key = `${m.kind}:${m.id}`
    if (seen.has(key)) continue
    if (!body.includes(`@${m.name}`)) continue
    seen.add(key)
    out.push(m)
  }
  return out
}

/** A posted body, cut into plain text and the mentions it names. */
export type BodyPiece = { kind: 'text'; text: string } | { kind: 'mention'; mention: MentionRef; text: string }

/**
 * Cut `body` at every `@Name` of its `mentions`. Longest names first, so
 * "@Claude (2)" is not taken for "@Claude" followed by " (2)". A name the body
 * does not contain is simply not drawn as a chip.
 *
 * Two mentions may share a name -- two people's agents are both "Claude"
 * (TASK-041 Phase 2) -- so the occurrences of one name are handed to its
 * mentions in the order the comment lists them, the order they were picked.
 */
export function segmentBody(body: string, mentions: MentionRef[] | null | undefined): BodyPiece[] {
  if (!mentions?.length) return body ? [{ kind: 'text', text: body }] : []
  const names = [...new Set(mentions.map((m) => m.name).filter(Boolean))].sort((a, b) => b.length - a.length)
  const queue = new Map<string, MentionRef[]>()
  for (const m of mentions) if (m.name) queue.set(m.name, [...(queue.get(m.name) ?? []), m])
  const out: BodyPiece[] = []
  let i = 0
  let plain = ''
  while (i < body.length) {
    let hit: MentionRef | null = null
    if (body[i] === '@') {
      const name = names.find((n) => body.startsWith(`@${n}`, i))
      if (name) {
        const waiting = queue.get(name)!
        // The last of a name keeps any further occurrences.
        hit = waiting.length > 1 ? waiting.shift()! : waiting[0]
      }
    }
    if (hit) {
      if (plain) out.push({ kind: 'text', text: plain })
      plain = ''
      const text = `@${hit.name}`
      out.push({ kind: 'mention', mention: hit, text })
      i += text.length
    } else {
      plain += body[i]
      i += 1
    }
  }
  if (plain) out.push({ kind: 'text', text: plain })
  return out
}

/**
 * What a chip's tooltip says: an agent names the person it acts for, so two
 * people's "Claude" chips stay apart. A person's chip needs none.
 */
export function mentionTitle(m: MentionRef): string | undefined {
  if (m.kind !== 'agent') return undefined
  return m.forName ? `${m.name}, for ${m.forName}` : m.name
}
